import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { LoadingState, StatTile } from "../../components/UI/States";
import { formatMinutes, jobLabel } from "./pcwApi";
import { useShopClock } from "./shopClocks";
import {
  AllottedBar,
  CsvButton,
  HeadRow,
  JobCell,
  MessageRow,
  SectionCard,
  csvHours,
  errorBanner,
} from "./shopClockUi";

// The shop clock's job schedule (the dashboard's Schedule tab, and the
// Schedule card on a job's page): due dates per job and which worker is
// assigned to which job for which days, with the hours planned and the
// hours they've actually punched. The tablet shows each worker today's
// assignments when they sign in; the shop floor TV's This Week and Deadlines
// screens show the same data. Server: routes/api/PCW/shopSchedule.js.
//
// Dates are calendar days ("YYYY-MM-DD", the shop's local days) — never
// pushed through Date/UTC, which would shift them a day.

// ---- Calendar days ----

const pad = (n) => String(n).padStart(2, "0");

const toDay = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const parseDay = (day) => {
  const [y, m, d] = String(day).split("-").map(Number);
  return new Date(y, m - 1, d);
};

export const todayDay = () => toDay(new Date());

const addDays = (day, days) => {
  const date = parseDay(day);
  date.setDate(date.getDate() + days);
  return toDay(date);
};

const daysBetween = (from, to) =>
  Math.round((parseDay(to) - parseDay(from)) / 86400000);

// Monday of the day's week.
const weekStartOf = (day) => addDays(day, -((parseDay(day).getDay() + 6) % 7));

export const fmtDay = (day, options = { weekday: "short", month: "short", day: "numeric" }) =>
  day ? parseDay(day).toLocaleDateString([], options) : "—";

const fmtRange = (start, end) =>
  start === end ? fmtDay(start) : `${fmtDay(start)} – ${fmtDay(end)}`;

// ---- Due dates ----

// Matches the shop floor TV: amber within 3 days, red once overdue.
export const dueInfo = (dueDate, today) => {
  const n = daysBetween(today, dueDate);
  if (n < 0)
    return { n, tone: "late", label: n === -1 ? "1 day overdue" : `${-n} days overdue` };
  if (n === 0) return { n, tone: "soon", label: "Due today" };
  if (n === 1) return { n, tone: "soon", label: "Due tomorrow" };
  return { n, tone: n <= 3 ? "soon" : "ok", label: `Due in ${n} days` };
};

const DUE_TONES = {
  late: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
  soon: "bg-amber-500/15 text-amber-800 dark:text-amber-300",
  ok: "bg-slate-500/15 text-slate-700 dark:text-slate-300",
};

export const DueChip = ({ dueDate, today, showDate = true }) => {
  if (!dueDate) return <span className="app-muted">—</span>;
  const { tone, label } = dueInfo(dueDate, today);
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${DUE_TONES[tone]}`}
    >
      {showDate ? `${fmtDay(dueDate)} · ${label}` : label}
    </span>
  );
};

// ---- Jobs as options ----

// "WO 139270 · Warsaw Admin Phase 1 · Fetters Constr" / "Shop Cleanup (custom job)"
const jobOptionLabel = (job) => {
  if (!job) return "";
  if (job.custom) return `${job.description || "Custom job"} (custom job)`;
  return [
    job.worksOrderNumber != null ? `WO ${job.worksOrderNumber}` : "",
    job.description,
    job.customerName,
  ]
    .filter((part) => part && String(part).trim())
    .join(" · ");
};

// An assignment's job as an option, for a job that's no longer open.
const optionFromWorkOrder = (key, workOrder) => ({
  key,
  custom: Boolean(workOrder?.custom),
  worksOrderNumber: workOrder?.custom ? null : workOrder?.worksOrderNumber ?? null,
  description: workOrder?.description || "",
  customerName: workOrder?.customerName || "",
});

// Job bar colors on the board, by job key so a job keeps its color.
const BAR_COLORS = [
  "56, 189, 248",
  "52, 211, 153",
  "251, 191, 36",
  "167, 139, 250",
  "244, 114, 182",
  "251, 146, 60",
];
const barColor = (key) => {
  let hash = 0;
  for (const ch of String(key)) hash = (hash * 31 + ch.charCodeAt(0)) % 9973;
  return BAR_COLORS[hash % BAR_COLORS.length];
};

const plannedText = (assignment) =>
  assignment.plannedMinutes
    ? `${formatMinutes(assignment.actualMinutes || 0)} of ${formatMinutes(
        assignment.plannedMinutes
      )}`
    : assignment.actualMinutes
    ? `${formatMinutes(assignment.actualMinutes)} worked`
    : "";

// ---- Dialogs ----

const DATE_FIELD_PROPS = { InputLabelProps: { shrink: true }, size: "small", type: "date" };

/**
 * Add or edit an assignment. `assignment` is an existing one (has _id) or a
 * prefill ({ workerId?, jobKey?, startDate?, endDate? }); `fixedJob`
 * ({ key, label }) pins the job (a job's own page).
 */
export const AssignmentDialog = ({
  open,
  assignment,
  jobs,
  workers,
  fixedJob,
  onSave,
  onDelete,
  onClose,
  saving,
  error,
}) => {
  const [workerId, setWorkerId] = useState("");
  const [jobKey, setJobKey] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [plannedHours, setPlannedHours] = useState("");
  const [note, setNote] = useState("");

  const editing = Boolean(assignment?._id);

  useEffect(() => {
    if (!open) return;
    const today = todayDay();
    setWorkerId(assignment?.workerId || "");
    setJobKey(fixedJob?.key || assignment?.jobKey || "");
    setStartDate(assignment?.startDate || today);
    setEndDate(assignment?.endDate || assignment?.startDate || today);
    setPlannedHours(
      assignment?.plannedMinutes
        ? String(Math.round((assignment.plannedMinutes / 60) * 100) / 100)
        : ""
    );
    setNote(assignment?.note || "");
    // Only when the dialog opens — later prop updates mustn't wipe typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Open jobs, plus the assignment's own job if it has since closed.
  const jobOptions = useMemo(() => {
    const list = [...(jobs || [])];
    if (assignment?.jobKey && !list.some((job) => job.key === assignment.jobKey))
      list.unshift(optionFromWorkOrder(assignment.jobKey, assignment.workOrder));
    return list;
  }, [jobs, assignment]);
  const selectedJob = jobOptions.find((job) => job.key === jobKey) || null;

  // Active workers, plus an inactive one already on this assignment.
  const workerOptions = (workers || []).filter(
    (worker) => worker.active || String(worker._id) === assignment?.workerId
  );

  const planned = plannedHours.trim() === "" ? null : Number(plannedHours);
  const plannedValid =
    planned === null || (Number.isFinite(planned) && planned > 0 && planned <= 10000);
  const datesValid = Boolean(startDate && endDate && endDate >= startDate);
  const valid = Boolean(workerId && jobKey && datesValid && plannedValid);
  const days = datesValid ? daysBetween(startDate, endDate) + 1 : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? "Edit Assignment" : "Assign a Worker"}</DialogTitle>
      <DialogContent>
        <div className="space-y-4 pt-2">
          {error && errorBanner(error)}
          <FormControl fullWidth size="small">
            <InputLabel id="assignment-worker">Worker</InputLabel>
            <Select
              labelId="assignment-worker"
              label="Worker"
              value={workerId}
              onChange={(e) => setWorkerId(e.target.value)}
            >
              {workerOptions.map((worker) => (
                <MenuItem key={worker._id} value={String(worker._id)}>
                  {worker.name}
                  {worker.active ? "" : " (inactive)"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {fixedJob ? (
            <TextField
              fullWidth
              size="small"
              label="Job"
              value={fixedJob.label}
              InputProps={{ readOnly: true }}
            />
          ) : (
            <Autocomplete
              options={jobOptions}
              value={selectedJob}
              onChange={(e, job) => setJobKey(job?.key || "")}
              getOptionLabel={jobOptionLabel}
              isOptionEqualToValue={(option, value) => option.key === value.key}
              renderOption={(props, job) => {
                const label = jobLabel(job);
                return (
                  <li {...props} key={job.key}>
                    <div className="min-w-0">
                      <div className="font-medium">
                        {label.title}
                        {job.custom && (
                          <span className="app-muted text-xs font-normal"> · custom job</span>
                        )}
                      </div>
                      <div className="app-muted truncate text-xs">
                        {[label.detail, job.customerName].filter(Boolean).join(" · ")}
                        {job.dueDate && ` · due ${fmtDay(job.dueDate)}`}
                      </div>
                    </div>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Job"
                  placeholder="Search work order #, job, or customer"
                />
              )}
            />
          )}

          <div className="grid grid-cols-2 gap-3">
            <TextField
              {...DATE_FIELD_PROPS}
              label="Start"
              value={startDate}
              onChange={(e) => {
                const value = e.target.value;
                setStartDate(value);
                // Keep the span valid when the start jumps past the end.
                if (value && endDate && value > endDate) setEndDate(value);
              }}
            />
            <TextField
              {...DATE_FIELD_PROPS}
              label="End"
              value={endDate}
              error={Boolean(startDate && endDate && endDate < startDate)}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <TextField
            fullWidth
            size="small"
            label="Planned hours (optional)"
            inputProps={{ inputMode: "decimal" }}
            value={plannedHours}
            error={!plannedValid}
            helperText={
              plannedValid
                ? `How long they should spend on it over ${
                    days === 1 ? "the day" : `these ${days || "…"} days`
                  } — the board shows hours worked against it.`
                : "Enter hours, like 8 or 12.5"
            }
            onChange={(e) =>
              setPlannedHours(e.target.value.replace(/[^\d.]/g, "").slice(0, 7))
            }
          />

          <TextField
            fullWidth
            size="small"
            label="Note (optional)"
            inputProps={{ maxLength: 200 }}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />

          {editing && (
            <p className="app-muted text-xs">
              Worked so far on this assignment:{" "}
              <span className="font-semibold tabular-nums">
                {formatMinutes(assignment.actualMinutes || 0)}
              </span>{" "}
              (their punches into this job between the start and end dates).
            </p>
          )}
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        {editing && onDelete && (
          <Button
            color="error"
            disabled={saving}
            onClick={() => onDelete(assignment)}
            sx={{ mr: "auto" }}
          >
            Remove
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!valid || saving}
          onClick={() =>
            onSave({
              workerId,
              jobKey,
              startDate,
              endDate,
              plannedMinutes: planned === null ? null : Math.round(planned * 60),
              note: note.trim(),
            })
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Set, change, or clear a job's due date. `job` ({ key, label, dueDate,
 * dateRequired }) fixes the job; without one the dialog asks for it from
 * `jobs`.
 */
export const DueDateDialog = ({ open, job, jobs, onSave, onClose, saving, error }) => {
  const [jobKey, setJobKey] = useState("");
  const [dueDate, setDueDate] = useState("");

  const picked = job || (jobs || []).find((option) => option.key === jobKey) || null;

  useEffect(() => {
    if (!open) return;
    setJobKey(job?.key || "");
    setDueDate(job?.dueDate || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Picking a job loads its current due date.
  const pickJob = (option) => {
    setJobKey(option?.key || "");
    setDueDate(option?.dueDate || "");
  };

  const bistrackDate = picked?.dateRequired
    ? String(picked.dateRequired).slice(0, 10)
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Due Date{job?.label ? ` — ${job.label}` : ""}</DialogTitle>
      <DialogContent>
        <div className="space-y-4 pt-2">
          {error && errorBanner(error)}
          {!job && (
            <Autocomplete
              options={jobs || []}
              value={picked}
              onChange={(e, option) => pickJob(option)}
              getOptionLabel={jobOptionLabel}
              isOptionEqualToValue={(option, value) => option.key === value.key}
              renderInput={(params) => (
                <TextField {...params} size="small" label="Job" />
              )}
            />
          )}
          <p className="app-muted text-sm">
            When the job has to be done. The shop floor TV's Deadlines screen
            counts down to it, and workers see it on their scheduled jobs at
            the tablet.
          </p>
          <TextField
            {...DATE_FIELD_PROPS}
            fullWidth
            label="Due date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
          {bistrackDate && bistrackDate !== dueDate && (
            <p className="text-sm">
              Bistrack's date required: {fmtDay(bistrackDate)}{" "}
              <Button size="small" onClick={() => setDueDate(bistrackDate)}>
                Use it
              </Button>
            </p>
          )}
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        {picked?.dueDate && (
          <Button color="error" disabled={saving} onClick={() => onSave(picked.key, null)}>
            Clear
          </Button>
        )}
        <Button
          variant="contained"
          disabled={!picked || !dueDate || saving}
          onClick={() => onSave(picked.key, dueDate)}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Saving/removing assignments and due dates — shared by the Schedule tab and
// a job's Schedule card.
const useScheduleActions = (user, onChanged) => {
  const { api } = useShopClock();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const run = async (action, onDone) => {
    setSaving(true);
    setError(null);
    try {
      await action();
      onDone?.();
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return {
    saving,
    error,
    clearError: () => setError(null),
    saveAssignment: (existing, fields, onDone) =>
      run(
        () =>
          existing?._id
            ? api.updateAssignment(existing._id, fields, user?.id)
            : api.createAssignment(fields, user?.id),
        onDone
      ),
    deleteAssignment: (assignment, onDone) => {
      if (
        !window.confirm(
          `Remove ${assignment.workerName}'s assignment (${fmtRange(
            assignment.startDate,
            assignment.endDate
          )})? Their punches aren't affected.`
        )
      )
        return;
      run(() => api.deleteAssignment(assignment._id, user?.id), onDone);
    },
    saveDueDate: (jobKey, dueDate, onDone) =>
      run(() => api.setDueDate(jobKey, dueDate, user?.id), onDone),
  };
};

// ---- Schedule tab ----

const ASSIGNMENT_CSV_HEADERS = [
  { label: "Worker", key: "worker" },
  { label: "Work Order", key: "workOrder" },
  { label: "Job", key: "job" },
  { label: "Custom Job", key: "customJob" },
  { label: "Customer", key: "customer" },
  { label: "Start", key: "start" },
  { label: "End", key: "end" },
  { label: "Planned Hours", key: "planned" },
  { label: "Worked Hours", key: "worked" },
  { label: "Due Date", key: "dueDate" },
  { label: "Note", key: "note" },
];

// Packs a worker's assignments into lanes so overlapping ones stack.
const toLanes = (assignments, days) => {
  const first = days[0];
  const last = days[days.length - 1];
  const laneEnds = [];
  return [...assignments]
    .sort((a, b) => a.startDate.localeCompare(b.startDate) || b.endDate.localeCompare(a.endDate))
    .map((assignment) => {
      const start = assignment.startDate < first ? first : assignment.startDate;
      const end = assignment.endDate > last ? last : assignment.endDate;
      let lane = laneEnds.findIndex((laneEnd) => laneEnd < start);
      if (lane === -1) lane = laneEnds.length;
      laneEnds[lane] = end;
      return {
        assignment,
        lane,
        column: days.indexOf(start) + 1,
        span: daysBetween(start, end) + 1,
        clippedLeft: assignment.startDate < first,
        clippedRight: assignment.endDate > last,
      };
    });
};

const WeekBoard = ({ days, today, rows, dueByDay, onAdd, onEdit }) => {
  const grid = { gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` };
  return (
    <div className="min-w-[760px]">
      <div className="app-divider flex border-b">
        <div className="w-36 shrink-0 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide app-muted">
          Worker
        </div>
        <div className="grid flex-1 gap-1 px-2 py-1.5" style={grid}>
          {days.map((day) => {
            const date = parseDay(day);
            const isToday = day === today;
            const due = dueByDay.get(day) || 0;
            return (
              <div
                key={day}
                className={`rounded-lg px-2 py-1 text-center ${
                  isToday ? "bg-sky-500/15 text-sky-800 dark:text-sky-300" : ""
                }`}
              >
                <div className="text-xs font-semibold uppercase tracking-wide">
                  {date.toLocaleDateString([], { weekday: "short" })} {date.getDate()}
                </div>
                {due > 0 && (
                  <div className="text-[11px] font-medium text-rose-700 dark:text-rose-300">
                    {due} due
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="app-muted px-4 py-10 text-center text-sm">
          No active workers yet — add them on the Workers tab.
        </div>
      ) : (
        rows.map(({ worker, assignments }) => {
          const placed = toLanes(assignments, days);
          const lanes = Math.max(1, ...placed.map((p) => p.lane + 1));
          return (
            <div key={worker._id} className="app-divider flex border-b last:border-b-0">
              <div className="w-36 shrink-0 px-4 py-3">
                <div className="truncate font-medium">{worker.name}</div>
                {!worker.active && <div className="app-muted text-xs">Inactive</div>}
              </div>
              <div className="relative flex-1 px-2 py-2">
                {/* Empty days: click to assign this worker starting that day */}
                <div className="absolute inset-0 grid gap-1 px-2 py-2" style={grid}>
                  {days.map((day) => (
                    <button
                      key={day}
                      type="button"
                      aria-label={`Assign ${worker.name} on ${fmtDay(day)}`}
                      onClick={() => onAdd({ workerId: String(worker._id), startDate: day, endDate: day })}
                      className={`group rounded-lg transition hover:bg-[color:var(--app-hover)] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 ${
                        day === today ? "bg-sky-500/5" : ""
                      }`}
                    >
                      <AddIcon
                        sx={{ fontSize: 16 }}
                        className="app-muted opacity-0 transition group-hover:opacity-100"
                      />
                    </button>
                  ))}
                </div>
                <div
                  className="pointer-events-none relative grid gap-1"
                  style={{ ...grid, gridTemplateRows: `repeat(${lanes}, minmax(44px, auto))` }}
                >
                  {placed.map(({ assignment, lane, column, span, clippedLeft, clippedRight }) => {
                    const label = jobLabel(assignment.workOrder);
                    const rgb = barColor(assignment.jobKey);
                    const hours = plannedText(assignment);
                    return (
                      <button
                        key={assignment._id}
                        type="button"
                        onClick={() => onEdit(assignment)}
                        title={[
                          `${label.title}${label.detail ? ` — ${label.detail}` : ""}`,
                          fmtRange(assignment.startDate, assignment.endDate),
                          hours,
                          assignment.note,
                        ]
                          .filter(Boolean)
                          .join("\n")}
                        className={`pointer-events-auto min-w-0 rounded-lg border-l-4 px-2 py-1 text-left transition hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 ${
                          clippedLeft ? "rounded-l-none border-dashed" : ""
                        } ${clippedRight ? "rounded-r-none" : ""}`}
                        style={{
                          gridColumn: `${column} / span ${span}`,
                          gridRow: lane + 1,
                          borderLeftColor: `rgb(${rgb})`,
                          background: `rgba(${rgb}, 0.16)`,
                        }}
                      >
                        <div className="truncate text-sm font-semibold">
                          {label.title}
                          {clippedRight && " ›"}
                        </div>
                        <div className="app-muted truncate text-xs">
                          {[label.custom ? "Custom job" : label.detail, hours]
                            .filter(Boolean)
                            .join(" · ") || " "}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export const ScheduleTab = ({ user, workers, reloadKey, onOpenJob }) => {
  const { api, shop } = useShopClock();
  const [weekOf, setWeekOf] = useState(() => weekStartOf(todayDay()));
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [version, setVersion] = useState(0);
  // null closed; {} / prefill to add; an assignment (with _id) to edit.
  const [dialogAssignment, setDialogAssignment] = useState(null);
  // undefined closed; null asks for the job; a job sets its date.
  const [dueJob, setDueJob] = useState(undefined);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekOf, i)),
    [weekOf]
  );

  const actions = useScheduleActions(user, () => setVersion((v) => v + 1));

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    api
      .fetchSchedule(user.id, { from: days[0], to: days[6] })
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setError(null);
      })
      .catch((err) => !cancelled && setError(err.message));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, days, reloadKey, version]);

  const today = data?.today || todayDay();
  const loaded = data && data.from === days[0];

  const jobs = useMemo(() => data?.jobs || [], [data]);
  const deadlines = useMemo(
    () =>
      jobs
        .filter((job) => job.dueDate)
        .sort((a, b) => a.dueDate.localeCompare(b.dueDate) || a.key.localeCompare(b.key)),
    [jobs]
  );
  const dueByDay = useMemo(() => {
    const map = new Map();
    deadlines.forEach((job) => map.set(job.dueDate, (map.get(job.dueDate) || 0) + 1));
    return map;
  }, [deadlines]);

  const assignments = useMemo(() => (loaded ? data.assignments : []), [loaded, data]);

  // Active workers, plus anyone inactive who still has something this week.
  const rows = useMemo(() => {
    const byWorker = new Map();
    assignments.forEach((a) => {
      if (!byWorker.has(a.workerId)) byWorker.set(a.workerId, []);
      byWorker.get(a.workerId).push(a);
    });
    return (workers || [])
      .filter((worker) => worker.active || byWorker.has(String(worker._id)))
      .map((worker) => ({ worker, assignments: byWorker.get(String(worker._id)) || [] }));
  }, [workers, assignments]);

  if (error && !data) return errorBanner(error);
  if (!data || !workers) return <LoadingState label="Loading the schedule…" />;

  const overdue = deadlines.filter((job) => job.dueDate < today);
  const dueThisWeek = deadlines.filter(
    (job) => job.dueDate >= days[0] && job.dueDate <= days[6]
  );
  const plannedMinutes = assignments.reduce((sum, a) => sum + (a.plannedMinutes || 0), 0);
  const isThisWeek = weekOf === weekStartOf(today);

  const openAssignment = (assignment) => {
    actions.clearError();
    setDialogAssignment(assignment);
  };
  const openDueDate = (job) => {
    actions.clearError();
    setDueJob(job);
  };

  return (
    <div className="space-y-6">
      {error && errorBanner(error)}
      {data.source === "recent" && (
        <div className="rounded-xl bg-amber-500/10 px-4 py-3 text-sm font-medium text-amber-800 dark:text-amber-300">
          Couldn't reach Bistrack for the open work order list — only jobs
          worked in the last 14 days can be scheduled right now.
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile
          icon={GroupsOutlinedIcon}
          value={new Set(assignments.map((a) => a.workerId)).size}
          label="Workers Scheduled"
        />
        <StatTile
          icon={TimerOutlinedIcon}
          tone="sky"
          value={formatMinutes(plannedMinutes)}
          label="Hours Planned"
        />
        <StatTile
          icon={EventOutlinedIcon}
          value={dueThisWeek.length}
          label="Due This Week"
        />
        <StatTile
          icon={WarningAmberOutlinedIcon}
          tone={overdue.length ? "rose" : "default"}
          value={overdue.length}
          label="Overdue"
        />
      </div>

      <SectionCard
        title={`Week of ${fmtDay(days[0], { month: "long", day: "numeric", year: "numeric" })}`}
        subtitle="Who is on which job and for how long. Click an empty day to assign someone, or a job bar to change it. Workers see today's jobs when they sign in at the tablet, and the shop floor TV shows this week."
        action={
          <>
            <div className="flex items-center gap-1">
              <Button
                size="small"
                aria-label="Previous week"
                onClick={() => setWeekOf(addDays(weekOf, -7))}
                sx={{ minWidth: 0 }}
              >
                <ChevronLeftIcon />
              </Button>
              <Button
                size="small"
                disabled={isThisWeek}
                onClick={() => setWeekOf(weekStartOf(today))}
              >
                This Week
              </Button>
              <Button
                size="small"
                aria-label="Next week"
                onClick={() => setWeekOf(addDays(weekOf, 7))}
                sx={{ minWidth: 0 }}
              >
                <ChevronRightIcon />
              </Button>
            </div>
            <CsvButton
              data={assignments.map((a) => {
                const label = jobLabel(a.workOrder);
                return {
                  worker: a.workerName,
                  workOrder: a.workOrder?.custom ? "" : a.workOrder?.worksOrderNumber ?? "",
                  job: a.workOrder?.description || "",
                  customJob: label.custom ? "yes" : "",
                  customer: a.workOrder?.customerName || "",
                  start: a.startDate,
                  end: a.endDate,
                  planned: a.plannedMinutes ? csvHours(a.plannedMinutes) : "",
                  worked: csvHours(a.actualMinutes),
                  dueDate: jobs.find((job) => job.key === a.jobKey)?.dueDate || "",
                  note: a.note,
                };
              })}
              headers={ASSIGNMENT_CSV_HEADERS}
              filename={`${shop.fileSlug}-schedule-${days[0]}.csv`}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => openAssignment({ startDate: isThisWeek ? today : days[0] })}
            >
              Assign Worker
            </Button>
          </>
        }
      >
        {loaded ? (
          <WeekBoard
            days={days}
            today={today}
            rows={rows}
            dueByDay={dueByDay}
            onAdd={openAssignment}
            onEdit={openAssignment}
          />
        ) : (
          <LoadingState label="Loading the week…" />
        )}
      </SectionCard>

      <SectionCard
        title="Deadlines"
        subtitle="Open jobs with a due date, soonest first. The shop floor TV's Deadlines screen counts down to them. Click a row for the job."
        action={
          <Button size="small" startIcon={<EventOutlinedIcon />} onClick={() => openDueDate(null)}>
            Set a Due Date
          </Button>
        }
      >
        <table className="app-table">
          <HeadRow columns={["Job", "Due", "On It", "Total Time", "Allotted", ""]} />
          <tbody>
            {deadlines.length === 0 ? (
              <MessageRow colSpan={6}>
                No due dates yet — set one here, on the Open Jobs tab, or on a
                job's page.
              </MessageRow>
            ) : (
              deadlines.map((job) => (
                <tr
                  key={job.key}
                  onClick={() => onOpenJob(job.key)}
                  onKeyDown={(e) => e.key === "Enter" && onOpenJob(job.key)}
                  tabIndex={0}
                  className="cursor-pointer focus-visible:bg-[color:var(--app-hover)]"
                >
                  <td className="max-w-[260px] px-4 py-3">
                    <JobCell workOrder={job} />
                    {job.customerName && !job.custom && (
                      <div className="app-muted truncate text-xs">{job.customerName}</div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <div className="text-sm font-medium">{fmtDay(job.dueDate)}</div>
                    <div className="mt-0.5">
                      <DueChip dueDate={job.dueDate} today={today} showDate={false} />
                    </div>
                  </td>
                  <td className="max-w-[220px] px-4 py-3">
                    {job.workingNow.length ? (
                      <span className="inline-flex items-center gap-1.5 font-medium text-emerald-700 dark:text-emerald-300">
                        <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-emerald-500" />
                        <span className="truncate">{job.workingNow.join(", ")}</span>
                      </span>
                    ) : job.scheduled.length ? (
                      <span className="truncate text-sm">
                        <span className="app-muted">Scheduled: </span>
                        {job.scheduled.join(", ")}
                      </span>
                    ) : (
                      <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
                        Nobody scheduled
                      </span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 font-semibold tabular-nums">
                    {formatMinutes(job.minutes)}
                  </td>
                  <td className="min-w-[160px] px-4 py-3">
                    {job.allottedMinutes ? (
                      <>
                        <div className="text-xs tabular-nums app-muted">
                          of {formatMinutes(job.allottedMinutes)}
                        </div>
                        <AllottedBar used={job.minutes} allotted={job.allottedMinutes} />
                      </>
                    ) : (
                      <span className="app-muted">—</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-right">
                    <Button
                      size="small"
                      startIcon={<EditOutlinedIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        openDueDate({ ...job, label: jobOptionLabel(job) });
                      }}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      Change
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </SectionCard>

      <AssignmentDialog
        open={dialogAssignment !== null}
        assignment={dialogAssignment}
        jobs={jobs}
        workers={workers}
        onSave={(fields) =>
          actions.saveAssignment(dialogAssignment, fields, () => setDialogAssignment(null))
        }
        onDelete={(assignment) =>
          actions.deleteAssignment(assignment, () => setDialogAssignment(null))
        }
        onClose={() => setDialogAssignment(null)}
        saving={actions.saving}
        error={actions.error}
      />

      <DueDateDialog
        open={dueJob !== undefined}
        job={dueJob}
        jobs={jobs}
        onSave={(jobKey, dueDate) =>
          actions.saveDueDate(jobKey, dueDate, () => setDueJob(undefined))
        }
        onClose={() => setDueJob(undefined)}
        saving={actions.saving}
        error={actions.error}
      />
    </div>
  );
};

// ---- A job's page: its due date and who is scheduled on it ----

/**
 * The Schedule card on a job's page. `summary` is the job's drill-down
 * summary ({ dueDate, dateRequired, assignments }); `onChanged` reloads it.
 */
export const JobScheduleCard = ({ jobKey, title, summary, workers, user, onChanged }) => {
  const [dialogAssignment, setDialogAssignment] = useState(null);
  const [editingDue, setEditingDue] = useState(false);
  const actions = useScheduleActions(user, onChanged);
  const today = todayDay();
  const assignments = summary?.assignments || [];
  const upcoming = assignments.filter((a) => a.endDate >= today);
  const past = assignments.filter((a) => a.endDate < today);

  const open = (assignment) => {
    actions.clearError();
    setDialogAssignment(assignment);
  };

  const row = (a) => (
    <tr key={a._id}>
      <td className="px-4 py-3 font-medium">{a.workerName}</td>
      <td className="whitespace-nowrap px-4 py-3">
        {fmtRange(a.startDate, a.endDate)}
        {a.startDate <= today && a.endDate >= today && (
          <span className="ml-2 rounded-full bg-sky-500/15 px-2 py-0.5 text-[11px] font-medium text-sky-800 dark:text-sky-300">
            Now
          </span>
        )}
      </td>
      <td className="min-w-[170px] px-4 py-3">
        <div className="text-sm tabular-nums">{plannedText(a) || "—"}</div>
        {a.plannedMinutes > 0 && (
          <AllottedBar used={a.actualMinutes || 0} allotted={a.plannedMinutes} />
        )}
      </td>
      <td className="app-muted max-w-[220px] truncate px-4 py-3 text-sm" title={a.note}>
        {a.note || "—"}
      </td>
      <td className="whitespace-nowrap px-4 py-2 text-right">
        <Button size="small" startIcon={<EditOutlinedIcon />} onClick={() => open(a)}>
          Edit
        </Button>
      </td>
    </tr>
  );

  return (
    <SectionCard
      title="Schedule"
      subtitle="This job's due date and who is assigned to it. Worked = their punches into this job during the assignment's dates."
      action={
        <>
          <Button
            size="small"
            startIcon={<EventOutlinedIcon />}
            onClick={() => {
              actions.clearError();
              setEditingDue(true);
            }}
          >
            {summary?.dueDate ? "Change Due Date" : "Set Due Date"}
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => open({ jobKey, startDate: today })}
          >
            Assign Worker
          </Button>
        </>
      }
    >
      <div className="app-divider flex flex-wrap items-center gap-3 border-b px-4 py-3 sm:px-5">
        <span className="app-eyebrow">Due</span>
        {summary?.dueDate ? (
          <DueChip dueDate={summary.dueDate} today={today} />
        ) : (
          <span className="app-muted text-sm">No due date set.</span>
        )}
        {summary?.dateRequired && (
          <span className="app-muted text-xs">
            Bistrack's date required: {fmtDay(String(summary.dateRequired).slice(0, 10))}
          </span>
        )}
      </div>
      <table className="app-table">
        <HeadRow columns={["Worker", "Dates", "Planned / Worked", "Note", ""]} />
        <tbody>
          {assignments.length === 0 ? (
            <MessageRow colSpan={5}>Nobody is scheduled on this job.</MessageRow>
          ) : (
            [...upcoming.slice().reverse(), ...past].map(row)
          )}
        </tbody>
      </table>

      <AssignmentDialog
        open={dialogAssignment !== null}
        assignment={dialogAssignment}
        workers={workers}
        fixedJob={{ key: jobKey, label: title }}
        onSave={(fields) =>
          actions.saveAssignment(dialogAssignment, fields, () => setDialogAssignment(null))
        }
        onDelete={(assignment) =>
          actions.deleteAssignment(assignment, () => setDialogAssignment(null))
        }
        onClose={() => setDialogAssignment(null)}
        saving={actions.saving}
        error={actions.error}
      />

      <DueDateDialog
        open={editingDue}
        job={{
          key: jobKey,
          label: title,
          dueDate: summary?.dueDate || null,
          dateRequired: summary?.dateRequired || null,
        }}
        onSave={(key, dueDate) => actions.saveDueDate(key, dueDate, () => setEditingDue(false))}
        onClose={() => setEditingDue(false)}
        saving={actions.saving}
        error={actions.error}
      />
    </SectionCard>
  );
};
